import jsPDF from "jspdf";
import dayjs from "dayjs";
import html2canvas from "html2canvas";
import { getInvoicePageLayout } from "../../utils/invoicePrintPagination";
import { generateInvoiceHTML } from "./InvoiceHTMLTemplate";
// @ts-ignore - Nécessaire car la bibliothèque n'a pas de types TypeScript corrects
import html2pdf from "jspdf-html2canvas";
import API_CONFIG from '../../config/api.js';
import QRCode from "qrcode";
import { buildInvoiceVerificationUrl } from "../../utils/invoiceVerificationUrl";
import { isInvoiceQrEnabled } from "../../utils/invoiceQrVisibility";
import apiClient from "../../api/apiClient";

// Format pour les nombres avec séparateur correct de milliers
const formatNumber = (value: any): string => {
  if (value === undefined || value === null || isNaN(Number(value))) {
    return "0";
  }
  // Utiliser le séparateur d'espace pour les milliers (format français standard)
  const formattedNumber = new Intl.NumberFormat("fr-FR", {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
    useGrouping: true,
  }).format(Number(value));

  // Log pour vérifier le format du nombre
  console.log(`Formatage du nombre ${value} en ${formattedNumber}`);

  return formattedNumber;
};

const resolveVerificationBaseUrl = () => {
  const publicBase =
    (process.env.REACT_APP_PUBLIC_BASE_URL || "").trim();
  const windowBase =
    typeof window !== "undefined" && window.location
      ? window.location.origin
      : "";
  const apiBase = (API_CONFIG?.BASE_URL || "").trim();

  let baseUrl = publicBase || windowBase || apiBase;

  if (!baseUrl || /localhost|127\.0\.0\.1/i.test(baseUrl)) {
    if (apiBase && !/localhost|127\.0\.0\.1/i.test(apiBase)) {
      baseUrl = apiBase;
    } else {
      baseUrl = "https://elsagestion.com";
    }
  }

  return baseUrl.replace(/\/$/, "");
};

interface GeneratePDFOptions {
  order: any;
  clients: any[];
  produits: any[];
  taxes: any[];
  companyName?: string;
  warehouseName?: string;
  warehouse?: any; // Ajout des informations complètes du magasin
  client?: any; // Ajout des informations complètes du client
}

export const generateInvoicePDF = async ({
  order,
  clients,
  produits,
  taxes,
  companyName = "ELSA Technologies",
  warehouseName = "ELSA Technologies",
  warehouse = null,
  client = null,
}: GeneratePDFOptions): Promise<void> => {
  // **LOGS DE DIAGNOSTIC COMPLETS**
  console.log("📄 [PDF DEBUG] === DÉBUT GÉNÉRATION PDF ===");
  console.log("📄 [PDF DEBUG] Environnement:", {
    userAgent:
      typeof navigator !== "undefined" ? navigator.userAgent : "Serveur",
    platform: typeof navigator !== "undefined" ? navigator.platform : "Serveur",
    language: typeof navigator !== "undefined" ? navigator.language : "Serveur",
  });

  console.log("📄 [PDF DEBUG] Produits reçus:", produits.length);
  console.log("📄 [PDF DEBUG] Données warehouse:", {
    name: warehouse?.name,
    hasLogo: !!warehouse?.logo,
    logoType: warehouse?.logo
      ? warehouse.logo.includes("data:")
        ? "base64"
        : "url"
      : "none",
  });
  console.log(
    "📄 [PDF DEBUG] Produits dans la commande:",
    order.produitsVendus?.length || 0
  );

  // **DIAGNOSTIC DES POLICES DISPONIBLES**
  if (typeof document !== "undefined") {
    console.log("📄 [PDF DEBUG] Polices système disponibles:");
    document.fonts.ready.then(() => {
      document.fonts.forEach((font) => {
        console.log(
          `📄 [PDF DEBUG] Police: ${font.family} - ${font.style} - ${font.weight}`
        );
      });
    });
  }

  // Fonctions utilitaires
  const getClient = (userId: number) => {
    // Utiliser le client fourni en priorité, sinon chercher dans la liste des clients
    if (client) return client;
    return clients.find((c) => c.id === userId) || null;
  };

  const getProductName = (productId: number) => {
    const product = produits.find(
      (p) => p.id_produit === productId || p.id === productId
    );
    return product ? product.nom_produit || product.name : "Produit inconnu";
  };

  // Fonction pour récupérer les détails d'unité
  const getUnitShortName = (unitId: number) => {
    const product = produits.find(
      (p) => p.id_produit === unitId || p.id === unitId
    );
    return product?.unit_short_name || "";
  };

  const toNumber = (value: any): number => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  // Calcul des totaux en évitant la double déduction des remises ligne
  const calculateTotals = () => {
    const items = Array.isArray(order.produitsVendus) ? order.produitsVendus : [];
    const itemSubtotal = items.reduce(
      (sum: number, item: any) => sum + toNumber(item.subtotal),
      0
    );
    const itemDiscount = items.reduce(
      (sum: number, item: any) =>
        sum + toNumber(item.discount_rate ?? item.remise),
      0
    );
    const orderDiscount = toNumber(order.discount);
    const totalDiscount = orderDiscount + itemDiscount;

    const itemTax = items.reduce(
      (sum: number, item: any) =>
        sum + toNumber(item.total_tax ?? item.montant_taxe),
      0
    );
    const rawOrderTax = Number(order.tax_amount);
    const totalTax = Number.isFinite(rawOrderTax) ? rawOrderTax : itemTax;

    const rawOrderSubtotal = Number(order.subtotal);
    const subtotal = Number.isFinite(rawOrderSubtotal)
      ? rawOrderSubtotal
      : itemSubtotal + itemDiscount;

    const rawOrderTotal = Number(order.total);
    const total = Number.isFinite(rawOrderTotal)
      ? rawOrderTotal
      : subtotal - totalDiscount + totalTax;

    return {
      subtotal,
      totalTax,
      totalDiscount,
      total,
    };
  };

  // Calculer les totaux
  const totals = calculateTotals();
  const clientObj = getClient(order.user_id);
  const clientName = clientObj ? clientObj.name : "Client inconnu";
  const paymentStatus = order.payment_status || "Non payé";
  const montantVerse = toNumber(order.paid_amount);
  const rawDueAmount = Number(order.due_amount);
  const montantDu = Number.isFinite(rawDueAmount)
    ? rawDueAmount
    : totals.total - montantVerse;
  const formattedDate = dayjs(order.order_date).format("DD-MM-YYYY");

  try {
    // Préparer les données pour le template HTML
    const warehouseId = order.warehouse_id || warehouse?.id || order.warehouse?.id;
    if (warehouseId) {
      const response = await apiClient.get(`/api/warehouses/${warehouseId}`, {
        params: { company_id: order.company_id || warehouse?.company_id },
      });
      warehouse = { ...warehouse, ...response.data };
    }
    // Generer le QR code de verification (optionnel)
    let qrCodeDataUrl = "";
    try {
      if (isInvoiceQrEnabled(warehouse) && order?.invoice_number && order?.verification_token) {
        const verificationUrl = buildInvoiceVerificationUrl({
          baseUrl: resolveVerificationBaseUrl(),
          invoiceNumber: order.invoice_number,
          verificationToken: order.verification_token,
        });
        qrCodeDataUrl = await QRCode.toDataURL(verificationUrl, {
          type: "image/png",
          width: 200,
          margin: 1,
          color: {
            dark: "#000000",
            light: "#FFFFFF",
          },
        });
      }
    } catch (qrError) {
      console.warn("Erreur generation QR code facture:", qrError);
      qrCodeDataUrl = "";
    }

    const templateData = {
      order,
      clientObj,
      warehouse,
      paymentStatus,
      montantVerse,
      montantDu,
      totals,
      formattedDate,
      formatNumber,
      qrCodeDataUrl,
    };

    // Générer le HTML de la facture
    console.log("📄 [PDF DEBUG] Génération du template HTML...");
    const invoiceHTML = generateInvoiceHTML(templateData);

    // Créer un élément div pour contenir le HTML
    const container = document.createElement("div");
    container.innerHTML = invoiceHTML;
    container.style.position = "absolute";
    container.style.left = "-9999px"; // Cache visuellement le conteneur
    document.body.appendChild(container);

    console.log("📄 [PDF DEBUG] HTML ajouté au DOM, analyse des styles...");

    // **DIAGNOSTIC DES STYLES ET POLICES APPLIQUÉS**
    setTimeout(() => {
      const invoiceBox = container.querySelector(".invoice-box") as HTMLElement;
      if (invoiceBox) {
        const computedStyle = window.getComputedStyle(invoiceBox);
        console.log("📄 [PDF DEBUG] Styles calculés pour .invoice-box:");
        console.log("📄 [PDF DEBUG] font-family:", computedStyle.fontFamily);
        console.log("📄 [PDF DEBUG] font-size:", computedStyle.fontSize);
        console.log("📄 [PDF DEBUG] font-weight:", computedStyle.fontWeight);
        console.log("📄 [PDF DEBUG] line-height:", computedStyle.lineHeight);

        // Vérifier les polices des éléments de texte
        const textElements = invoiceBox.querySelectorAll("td, th, div");
        textElements.forEach((el, index) => {
          if (index < 5) {
            // Limiter aux 5 premiers pour éviter le spam
            const style = window.getComputedStyle(el);
            console.log(
              `📄 [PDF DEBUG] Élément ${index + 1} font-family:`,
              style.fontFamily
            );
          }
        });
      }
    }, 100);

    // Précharger les images pour s'assurer qu'elles sont disponibles avant la génération du PDF
    const preloadImages = () => {
      return new Promise<void>((resolve) => {
        const images = container.querySelectorAll("img");
        let loadedCount = 0;

        if (images.length === 0) {
          console.log("Aucune image à précharger");
          resolve();
          return;
        }

        images.forEach((img) => {
          const imgElement = img as HTMLImageElement;
          if (imgElement.complete) {
            loadedCount++;
            console.log(`Image ${loadedCount}/${images.length} déjà chargée`);
            if (loadedCount === images.length) {
              console.log("Toutes les images sont chargées");
              resolve();
            }
          } else {
            imgElement.onload = () => {
              loadedCount++;
              console.log(`Image ${loadedCount}/${images.length} chargée`);
              if (loadedCount === images.length) {
                console.log("Toutes les images sont chargées");
                resolve();
              }
            };
            imgElement.onerror = () => {
              console.error(
                "Erreur lors du chargement de l'image:",
                imgElement.src
              );
              loadedCount++;
              if (loadedCount === images.length) {
                console.log("Toutes les images sont chargées (avec erreurs)");
                resolve();
              }
            };
          }
        });
      });
    };

    // Attendre que les images soient chargées, puis générer le PDF
    await preloadImages();

    // **NOUVEAU SYSTÈME DE CHARGEMENT DES POLICES OPTIMISÉ**
    console.log("📄 [PDF DEBUG] Début du chargement optimisé des polices...");

    // Attendre que les polices CSS soient chargées avant la capture
    if (document.fonts && document.fonts.ready) {
      console.log("📄 [PDF DEBUG] Attente chargement des polices CSS...");
      await document.fonts.ready;
      console.log(
        "📄 [PDF DEBUG] Polices CSS chargées. Status:",
        document.fonts.status
      );
      console.log(
        "📄 [PDF DEBUG] Vérification de la police 'Inter':",
        document.fonts.check("1em Inter")
      );
      document.fonts.forEach((font) =>
        console.log(
          "📄 [PDF DEBUG] Police chargée:",
          font.family,
          font.style,
          font.weight
        )
      );
    }

    // **PRÉCHARGEMENT MANUEL DES POLICES**
    const baseUrl = API_CONFIG.BASE_URL;

    console.log(
      "📄 [FONT PRELOAD] Début du préchargement manuel des polices..."
    );

    const fontUrls = [
      `${baseUrl}/fonts/Inter-Light-subset.woff2`,
      `${baseUrl}/fonts/Inter-Regular-subset.woff2`,
      `${baseUrl}/fonts/Inter-Medium-subset.woff2`,
      `${baseUrl}/fonts/Inter-SemiBold-subset.woff2`,
      `${baseUrl}/fonts/Inter-Bold-subset.woff2`,
    ];

    const fontWeights = [300, 400, 500, 600, 700];

    for (let i = 0; i < fontUrls.length; i++) {
      try {
        if ("FontFace" in window) {
          const fontFace = new FontFace("Inter", `url(${fontUrls[i]})`, {
            weight: fontWeights[i].toString(),
            style: "normal",
            display: "block",
          });

          await fontFace.load();
          document.fonts.add(fontFace);
          console.log(
            `📄 [FONT PRELOAD] Police Inter ${fontWeights[i]} chargée avec succès`
          );
        }
      } catch (error) {
        console.warn(
          `📄 [FONT PRELOAD] Erreur lors du chargement de la police ${fontWeights[i]}:`,
          error
        );
      }
    }

    // **DÉLAI D'ATTENTE SUPPLÉMENTAIRE POUR S'ASSURER QUE LES POLICES SONT APPLIQUÉES**
    console.log(
      "📄 [PDF DEBUG] Attente supplémentaire pour l'application des polices..."
    );
    await new Promise((resolve) => setTimeout(resolve, 1000));

    // **VÉRIFICATION FINALE DES POLICES**
    console.log("📄 [FONT VERIFY] Vérification des polices chargées...");

    let loadedCount = 0;

    for (const weight of fontWeights) {
      if (document.fonts && document.fonts.check(`${weight} 1em Inter`)) {
        loadedCount++;
        console.log(`📄 [FONT VERIFY] Police Inter ${weight} confirmée`);
      } else {
        console.warn(`📄 [FONT VERIFY] Police Inter ${weight} non trouvée`);
      }
    }

    console.log(
      `📄 [FONT VERIFY] ${loadedCount}/${fontWeights.length} polices Inter chargées`
    );

    // Vérification des styles calculés
    const testElement = document.createElement("div");
    testElement.style.fontFamily = "Inter, Arial, sans-serif";
    testElement.style.fontSize = "12px";
    testElement.innerHTML = "Test Inter Font";
    testElement.style.position = "absolute";
    testElement.style.left = "-9999px";
    document.body.appendChild(testElement);

    const computedStyle = window.getComputedStyle(testElement);
    console.log(
      `📄 [FONT VERIFY] Police calculée: ${computedStyle.fontFamily}`
    );

    document.body.removeChild(testElement);

    console.log("📄 [PDF DEBUG] === DÉBUT GÉNÉRATION PDF AVEC HTML2CANVAS ===");

    // Méthode alternative avec html2canvas (haute qualité) + jsPDF
    const element = container.querySelector(".invoice-box") as HTMLElement;

    if (!element) {
      throw new Error("Element .invoice-box introuvable dans le DOM");
    }

    console.log("📄 [PDF DEBUG] Dimensions de l'élément:", {
      width: element.offsetWidth,
      height: element.offsetHeight,
      scrollWidth: element.scrollWidth,
      scrollHeight: element.scrollHeight,
    });

    console.log("📄 [PDF DEBUG] Configuration html2canvas optimisée...");
    const canvas = await html2canvas(element, {
      scale: 2, // Double la résolution pour une meilleure qualité
      useCORS: true,
      logging: true, // Activer les logs html2canvas pour le diagnostic
      allowTaint: true,
      imageTimeout: 15000, // Augmenter le délai d'attente pour les images
      backgroundColor: "#ffffff",
      removeContainer: false,
      foreignObjectRendering: true, // Améliore le rendu des polices
      width: element.scrollWidth, // Utilise la largeur de défilement
      height: element.scrollHeight, // Utilise la hauteur de défilement
      x: 0,
      y: 0,
      scrollX: 0,
      scrollY: 0,
      onclone: (clonedDoc) => {
        // Applique les polices sur le document cloné
        console.log(
          "📄 [PDF DEBUG] Application des polices sur le document cloné..."
        );

        const clonedElement = clonedDoc.querySelector(".invoice-box");
        if (clonedElement) {
          (clonedElement as HTMLElement).style.fontFamily =
            "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";

          // Applique les polices à tous les éléments enfants
          const allElements = clonedElement.querySelectorAll("*");
          allElements.forEach((el) => {
            (el as HTMLElement).style.fontFamily =
              "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";
          });
        }
      },
    });

    console.log("📄 [PDF DEBUG] Canvas généré:", {
      width: canvas.width,
      height: canvas.height,
      dataURL: canvas.toDataURL("image/jpeg", 1.0).substring(0, 50) + "...",
    });

    // Créer un nouveau document PDF avec une meilleure qualité
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
      compress: true,
    });

    // Le template contient déjà les marges A4 : ne pas le réduire une seconde fois.
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const pixelHeightMm = pageWidth / canvas.width;
    const canvasScale = canvas.width / element.scrollWidth;
    const pageHeightPixels = Math.floor(pageHeight / pixelHeightMm);
    const pageMarginPixels = Math.ceil(10 / pixelHeightMm);
    const { slices, footer, declaration, footerPageTop, tableHeader } = getInvoicePageLayout(element, {
      pageHeightPixels, pageMarginPixels, canvasScale,
    });

    for (let pageIndex = 0; pageIndex < slices.length; pageIndex++) {
      if (pageIndex > 0) pdf.addPage();
      const slice = slices[pageIndex];
      const pageCanvas = document.createElement("canvas");
      pageCanvas.width = canvas.width;
      pageCanvas.height = pageHeightPixels;
      const context = pageCanvas.getContext("2d");
      if (!context) throw new Error("Impossible de préparer la page de facture");
      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
      if (slice.repeatHeader) context.drawImage(canvas, 0, tableHeader.top, canvas.width, tableHeader.height, 0, pageMarginPixels, canvas.width, tableHeader.height);
      context.drawImage(canvas, 0, slice.top, canvas.width, slice.height, 0, slice.targetTop, canvas.width, slice.height);
      if (footer.height > 0) context.drawImage(canvas, 0, footer.top, canvas.width, footer.height, 0, footerPageTop, canvas.width, footer.height);
      if (declaration.height > 0 && pageIndex === slices.length - 1) {
        context.drawImage(canvas, 0, declaration.top, canvas.width, declaration.height, 0, footerPageTop - declaration.height, canvas.width, declaration.height);
      }
      pdf.addImage(pageCanvas.toDataURL("image/jpeg", 1.0), "JPEG", 0, 0, pageWidth, pageHeight);
      pdf.setFontSize(8);
      pdf.setTextColor(115, 128, 153);
      pdf.text(`Page ${pageIndex + 1} sur ${slices.length}`, pageWidth - 12.7, pageHeight - 5.6, { align: "right" });
    }

    // Télécharger le PDF
    pdf.save(`Facture-${order.invoice_number}.pdf`);

    console.log("PDF généré avec succès");

    // Nettoyer le DOM après un court délai
    setTimeout(() => {
      try {
        document.body.removeChild(container);
        console.log("Conteneur HTML supprimé du DOM");
      } catch (error) {
        console.error("Erreur lors du nettoyage du DOM:", error);
      }
    }, 1000);

    return Promise.resolve();
  } catch (error) {
    console.error("Erreur globale lors de la génération de la facture:", error);
    return Promise.reject(error);
  }
};
